/**
 * Investor Reporting - Per-investor and by-market reporting with filter panel
 *
 * Route: /admin/reporting/investors
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { format, formatDistanceToNow, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { DatePicker } from '@/common/components/date-picker'
import { Multiselect } from '@/common/components/multiselect'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Icon } from '@/common/components/icon'
import { useAuth } from '@/common/hooks/useAuth'
import { useMarkets } from '@/common/hooks/useMarkets'
import { useInvestorList, useInvestorsByMarket } from '@/features/reporting/hooks/useReportingData'
import { fetchInvestorList, fetchMarketActivityRecords, type InvestorListItem, type InvestorsByMarketItem, type InvestorListParams, type MarketActivityRecordItem } from '@/features/reporting/api/reportingApi'
import { mapInvestorsByMarketPage, investorsByMarketFilename } from '@/features/reporting/utils/investorExportUtils'
import { Tooltip as InfoTooltip } from '@/common/components/tooltip'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { table } from '@/features/reporting/chartPalette'
import * as styles from '../index.css'

// =============================================================================
// Constants
// =============================================================================

type SortDir = 'asc' | 'desc'
type TabValue = 'individual' | 'by-market'
type TimeFramePreset = 'all' | 'last_7' | 'last_30' | 'last_month' | 'this_month' | 'last_year' | 'this_year' | 'custom'
type DrillDownType = 'investors' | 'active' | 'engaged' | 'offers' | 'inquiries' | 'uc' | 'closed'

const INVESTOR_DRILL_TYPES = new Set<DrillDownType>(['investors', 'active', 'engaged'])
const ACTIVITY_DRILL_TYPES = new Set<DrillDownType>(['offers', 'inquiries', 'uc', 'closed'])

const DRILL_TYPE_TO_RECORD_TYPE: Record<string, string> = {
  offers: 'offers',
  inquiries: 'inquiries',
  uc: 'uc',
  closed: 'closed',
}

const TIME_FRAME_OPTIONS: { value: TimeFramePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom' },
]

function resolveTimeFrame(
  preset: TimeFramePreset,
  customStart?: Date | null,
  customEnd?: Date | null,
): { start?: string; end?: string } {
  const today = new Date()
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
  switch (preset) {
    case 'all':
      return {}
    case 'last_7':
      return { start: fmt(subDays(today, 7)) }
    case 'last_30':
      return { start: fmt(subDays(today, 30)) }
    case 'last_month': {
      const prev = subMonths(today, 1)
      return { start: fmt(startOfMonth(prev)), end: fmt(endOfMonth(prev)) }
    }
    case 'this_month':
      return { start: fmt(startOfMonth(today)) }
    case 'last_year': {
      const prev = subYears(today, 1)
      return { start: fmt(startOfYear(prev)), end: fmt(endOfYear(prev)) }
    }
    case 'this_year':
      return { start: fmt(startOfYear(today)) }
    case 'custom':
      return {
        start: customStart ? fmt(customStart) : undefined,
        end: customEnd ? fmt(customEnd) : undefined,
      }
  }
}

const STATUS_OPTIONS = [
  { value: 'engaged', label: 'Engaged' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
]


const COLUMN_TOOLTIPS: Record<string, string> = {
  name: 'Investor\'s full name from their COM platform profile (Source: Platform)',
  company_name: 'Investor\'s business or company name (Source: Platform)',
  email: 'Registered email address (Source: Platform)',
  inquiries: 'Total information requests submitted in the selected date range (Source: Platform Activity)',
  offers: 'Total cash offers submitted in the selected date range (Source: Platform Activity)',
  contracts_uc: 'Properties under contract where this investor holds the accepted offer (Source: Platform)',
  closed: 'Sold properties where this investor held the accepted offer (Source: Platform)',
  close_rate: 'Closed / (UC + Closed) as a percentage (Source: Platform)',
  last_login: 'Last time this investor logged into the platform (Source: Platform)',
  last_engagement: 'Most recent offer or inquiry submission (all time) (Source: Platform Activity)',
  buybox_count: 'Number of active buy boxes with their configured markets (Source: Platform)',
  status: 'Investor lifecycle status: Invited (account created, no agreement signed), Active (signed agreement, no offer/inquiry in last 90 days), Engaged (submitted an offer or inquiry within the last 90 days from today — this is a rolling window, not affected by date filters). (Source: Platform)',
  onboarding_date: 'Date investor completed onboarding (Source: Platform)',
  invited_date: 'Date investor account was created (Source: Platform)',
  days_to_first_submission: 'Days from completing onboarding to first offer or inquiry (Source: Platform Activity)',
  annual_purchase_volume: 'Self-reported annual property purchase tier (Source: Platform)',
  signup_source: 'How the investor joined: invited by admin or requested access (Source: Platform)',
  agreement_version: 'Current signed agreement version, or blank if unsigned (Source: Platform)',
}

const BY_MARKET_TOOLTIPS: Record<string, string> = {
  market: 'Market name (Source: Platform)',
  investors: 'Total number of onboarded investors in this market (Source: Platform)',
  active: 'Investors who signed the agreement but have never submitted an offer or inquiry (Source: Platform)',
  engaged: 'Investors who submitted an offer or inquiry within the last 90 days from today (rolling window, not affected by date filters). (Source: Platform Activity)',
  offers: 'Total cash offers submitted by investors in this market (Source: Platform Activity)',
  inquiries: 'Total information requests submitted by investors in this market (Source: Platform Activity)',
  uc: 'Properties under contract with an accepted platform offer in this market (Source: Platform)',
  closed: 'Sold properties with an accepted platform offer in this market (Source: Platform)',
  close_rate: 'Closed / (UC + Closed) as a percentage (Source: Platform)',
}

interface ColumnDef {
  key: string
  label: string
  sortKey?: string
  align?: 'left' | 'right'
  optional?: boolean
  render: (inv: InvestorListItem) => string | React.ReactNode
}

const DEFAULT_VISIBLE = ['name', 'email', 'inquiries', 'offers', 'contracts_uc', 'closed', 'close_rate']

const LOCAL_STORAGE_KEY = 'investor-report-columns'

function getStoredColumns(): string[] {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as string[]
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE
}

// =============================================================================
// Helpers
// =============================================================================

function formatLastLogin(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
}

function formatCloseRate(rate: number | null): string {
  if (rate === null) return 'N/A'
  return `${rate.toFixed(1)}%`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return format(new Date(dateStr), 'MMM d, yyyy')
}

function formatStatusBadges(statuses: string[]): React.ReactNode {
  if (!statuses.length) return '-'
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {statuses.map((s) => (
        <span
          key={s}
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 500,
            backgroundColor: s === 'engaged' ? '#DCFCE7' : s === 'active' ? '#DBEAFE' : s === 'invited' ? '#F3F4F6' : '#F3F4F6',
            color: s === 'engaged' ? '#166534' : s === 'active' ? '#1E40AF' : s === 'invited' ? '#374151' : '#374151',
          }}
        >
          {STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s}
        </span>
      ))}
    </div>
  )
}

// Info icon SVG reused across tooltips
const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" />
  </svg>
)

function ColumnHeader({ label, tooltip, sortIndicator, align = 'left' }: {
  label: string
  tooltip: string
  sortIndicator?: string
  align?: 'left' | 'right'
}) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: '3px' }}>
      {label}{sortIndicator}
      <InfoTooltip content={tooltip} side="top" delayDuration={200}>
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
          <InfoIcon />
        </span>
      </InfoTooltip>
    </div>
  )
}

// =============================================================================
// Route
// =============================================================================

export const Route = createFileRoute({
  component: InvestorsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (['individual', 'by-market'].includes(search.tab as string) ? (search.tab as TabValue) : 'individual'),
    sortBy: (search.sortBy as string) || 'last_login',
    sortDir: (['asc', 'desc'].includes(search.sortDir as string) ? (search.sortDir as SortDir) : 'desc'),
  }),
})

// =============================================================================
// Main Component
// =============================================================================

function InvestorsPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const searchParams = Route.useSearch()
  const { tab, sortBy, sortDir } = searchParams

  const updateSearch = useCallback(
    (updates: Partial<typeof searchParams>) => {
      void navigate({ to: '.', search: { ...searchParams, ...updates }, replace: true })
    },
    [navigate, searchParams]
  )

  // --- Filter state ---
  const [filterMarkets, setFilterMarkets] = useState<string[]>([])
  const [filterStatuses, setFilterStatuses] = useState<string[]>([])
  const [onboardedTimeFrame, setOnboardedTimeFrame] = useState<TimeFramePreset>('all')
  const [onboardedCustomStart, setOnboardedCustomStart] = useState<Date | null>(null)
  const [onboardedCustomEnd, setOnboardedCustomEnd] = useState<Date | null>(null)
  const [loginTimeFrame, setLoginTimeFrame] = useState<TimeFramePreset>('all')
  const [loginCustomStart, setLoginCustomStart] = useState<Date | null>(null)
  const [loginCustomEnd, setLoginCustomEnd] = useState<Date | null>(null)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Applied filters (only update on Apply click)
  const [appliedFilters, setAppliedFilters] = useState<{
    market?: string[]
    status?: string[]
    onboarded_start?: string
    onboarded_end?: string
    login_start?: string
    login_end?: string
    search?: string
  }>({})

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns)
  const [showColumnPicker, setShowColumnPicker] = useState(false)

  // Drill-down state
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null)
  const [drillDownType, setDrillDownType] = useState<DrillDownType>('investors')
  const [drillDownPage, setDrillDownPage] = useState(1)

  // Markets data
  const { groupedMarketOptions, loading: marketsLoading } = useMarkets()

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchText])

  // Reset page when applied filters change
  useEffect(() => {
    setPage(1)
  }, [appliedFilters, sortBy, sortDir])

  // Reset drill-down when filters change
  useEffect(() => {
    setSelectedMarket(null)
    setDrillDownPage(1)
  }, [appliedFilters])

  // Apply filters handler
  const handleApplyFilters = useCallback(() => {
    const onboarded = resolveTimeFrame(onboardedTimeFrame, onboardedCustomStart, onboardedCustomEnd)
    const login = resolveTimeFrame(loginTimeFrame, loginCustomStart, loginCustomEnd)
    setAppliedFilters({
      market: filterMarkets.length > 0 ? filterMarkets : undefined,
      status: filterStatuses.length > 0 ? filterStatuses : undefined,
      onboarded_start: onboarded.start,
      onboarded_end: onboarded.end,
      login_start: login.start,
      login_end: login.end,
      search: debouncedSearch || undefined,
    })
    setPage(1)
  }, [filterMarkets, filterStatuses, onboardedTimeFrame, onboardedCustomStart, onboardedCustomEnd, loginTimeFrame, loginCustomStart, loginCustomEnd, debouncedSearch])

  // Clear filters handler
  const handleClearFilters = useCallback(() => {
    setFilterMarkets([])
    setFilterStatuses([])
    setOnboardedTimeFrame('all')
    setOnboardedCustomStart(null)
    setOnboardedCustomEnd(null)
    setLoginTimeFrame('all')
    setLoginCustomStart(null)
    setLoginCustomEnd(null)
    setSearchText('')
    setDebouncedSearch('')
    setAppliedFilters({})
    setPage(1)
  }, [])

  // Auto-apply search on debounce
  useEffect(() => {
    setAppliedFilters((prev) => ({ ...prev, search: debouncedSearch || undefined }))
  }, [debouncedSearch])

  // --- API params ---
  const apiParams: InvestorListParams = useMemo(() => ({
    ...appliedFilters,
    sort_by: sortBy,
    sort_dir: sortDir,
    page,
    page_size: pageSize,
  }), [appliedFilters, sortBy, sortDir, page, pageSize])

  const { data: investorsRes, isFetching } = useInvestorList(apiParams)
  const byMarketFilters = useMemo(() => ({
    onboarded_start: appliedFilters.onboarded_start,
    onboarded_end: appliedFilters.onboarded_end,
    login_start: appliedFilters.login_start,
    login_end: appliedFilters.login_end,
  }), [appliedFilters])
  const { data: byMarketRes, isFetching: byMarketFetching } = useInvestorsByMarket(byMarketFilters)

  const investors = useMemo(() => investorsRes?.data ?? [], [investorsRes?.data])
  const pagination = investorsRes?.pagination
  const byMarketData = useMemo(() => byMarketRes?.data ?? [], [byMarketRes?.data])

  // --- Drill-down queries ---
  const isInvestorDrill = INVESTOR_DRILL_TYPES.has(drillDownType)
  const isActivityDrill = ACTIVITY_DRILL_TYPES.has(drillDownType)

  // Investor drill-down params (investors/active/engaged)
  const investorDrillParams = useMemo(() => {
    if (!selectedMarket || !isInvestorDrill) return null
    const statusFilter = drillDownType === 'active' ? ['active'] : drillDownType === 'engaged' ? ['engaged'] : undefined
    return {
      ...appliedFilters,
      market: [selectedMarket],
      status: statusFilter ?? appliedFilters.status,
      sort_by: sortBy,
      sort_dir: sortDir,
      page: drillDownPage,
      page_size: 25,
    }
  }, [selectedMarket, isInvestorDrill, drillDownType, appliedFilters, sortBy, sortDir, drillDownPage])

  const { data: investorDrillRes, isFetching: investorDrillFetching } = useQuery({
    queryKey: ['reporting', 'investors', 'drill-down', investorDrillParams],
    queryFn: () => fetchInvestorList(token!, investorDrillParams!),
    enabled: !!token && !!investorDrillParams,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  // Activity drill-down params (offers/inquiries/uc/closed)
  const activityDrillParams = useMemo(() => {
    if (!selectedMarket || !isActivityDrill) return null
    return {
      market: selectedMarket,
      record_type: DRILL_TYPE_TO_RECORD_TYPE[drillDownType],
      start_date: appliedFilters.onboarded_start,
      end_date: appliedFilters.onboarded_end,
      page: drillDownPage,
      page_size: 25,
    }
  }, [selectedMarket, isActivityDrill, drillDownType, appliedFilters, drillDownPage])

  const { data: activityDrillRes, isFetching: activityDrillFetching } = useQuery({
    queryKey: ['reporting', 'market-activity', 'drill-down', activityDrillParams],
    queryFn: () => fetchMarketActivityRecords(token!, activityDrillParams!),
    enabled: !!token && !!activityDrillParams,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  const drillDownFetching = investorDrillFetching || activityDrillFetching
  const drillDownPagination = isInvestorDrill ? investorDrillRes?.pagination : activityDrillRes?.pagination

  // --- Sort ---
  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      updateSearch({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })
    } else {
      updateSearch({ sortBy: column, sortDir: 'desc' })
    }
  }, [sortBy, sortDir, updateSearch])

  const sortIndicator = (column: string) => {
    if (sortBy !== column) return ''
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'
  }

  // --- Column config ---
  const allColumns: ColumnDef[] = useMemo(() => [
    { key: 'name', label: 'Investor Name', sortKey: 'name', render: (inv) => `${inv.first_name} ${inv.last_name}` },
    { key: 'company_name', label: 'Business Name', optional: true, render: (inv) => inv.company_name ?? '-' },
    { key: 'email', label: 'Email', sortKey: 'email', render: (inv) => inv.email ?? '-' },
    { key: 'inquiries', label: 'Inquiries', sortKey: 'inquiries', align: 'right', render: (inv) => inv.inquiries.toLocaleString() },
    { key: 'offers', label: 'Offers', sortKey: 'offers', align: 'right', render: (inv) => inv.offers.toLocaleString() },
    { key: 'contracts_uc', label: 'UC', sortKey: 'contracts_uc', align: 'right', render: (inv) => inv.contracts_uc.toLocaleString() },
    { key: 'closed', label: 'Closed', sortKey: 'closed', align: 'right', render: (inv) => inv.closed.toLocaleString() },
    { key: 'close_rate', label: 'Close Rate', sortKey: 'close_rate', align: 'right', render: (inv) => formatCloseRate(inv.close_rate) },
    { key: 'last_login', label: 'Last Login', sortKey: 'last_login', optional: true, render: (inv) => formatLastLogin(inv.last_login_at) },
    { key: 'last_engagement', label: 'Last Engagement', sortKey: 'last_engagement', optional: true, render: (inv) => formatDate(inv.last_engagement_date) },
    { key: 'buybox_count', label: '# Buy Boxes', sortKey: 'buybox_count', align: 'right', optional: true, render: (inv) => {
      const markets = inv.buybox_markets?.join(', ') ?? ''
      return inv.buybox_count > 0 ? `${inv.buybox_count} (${markets})` : '0'
    }},
    { key: 'status', label: 'Status', sortKey: 'status', optional: true, render: (inv) => formatStatusBadges(inv.statuses) },
    { key: 'onboarding_date', label: 'Onboarding Date', sortKey: 'onboarding_date', optional: true, render: (inv) => formatDate(inv.onboarding_date) },
    { key: 'invited_date', label: 'Invited Date', sortKey: 'invited_date', optional: true, render: (inv) => formatDate(inv.invited_date) },
    { key: 'days_to_first_submission', label: 'Days to 1st Submission', sortKey: 'days_to_first_submission', align: 'right', optional: true, render: (inv) => inv.days_onboarded_to_first_submission !== null ? String(inv.days_onboarded_to_first_submission) : '-' },
    { key: 'annual_purchase_volume', label: 'Annual Purchases Tier', sortKey: 'annual_purchase_volume', optional: true, render: (inv) => inv.annual_purchase_volume ?? '-' },
    { key: 'signup_source', label: 'Signup Source', sortKey: 'signup_source', optional: true, render: (inv) => inv.signup_source ?? '-' },
    { key: 'agreement_version', label: 'Agreement Version', sortKey: 'agreement_version', optional: true, render: (inv) => inv.agreement_version ?? '-' },
  ], [])

  const activeColumns = useMemo(
    () => visibleColumns
      .map((key) => allColumns.find((col) => col.key === key))
      .filter((col): col is ColumnDef => col != null),
    [allColumns, visibleColumns]
  )

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  // Column drag-to-reorder
  const [draggedCol, setDraggedCol] = useState<string | null>(null)
  const [dragOverCol, setDragOverCol] = useState<string | null>(null)

  const handleColDragStart = useCallback((key: string) => {
    setDraggedCol(key)
  }, [])

  const handleColDragOver = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault()
    setDragOverCol(key)
  }, [])

  const handleColDrop = useCallback((targetKey: string) => {
    if (!draggedCol || draggedCol === targetKey) {
      setDraggedCol(null)
      setDragOverCol(null)
      return
    }
    setVisibleColumns((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(draggedCol)
      const toIdx = next.indexOf(targetKey)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(fromIdx, 1)
      next.splice(toIdx, 0, draggedCol)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next))
      return next
    })
    setDraggedCol(null)
    setDragOverCol(null)
  }, [draggedCol])

  const handleColDragEnd = useCallback(() => {
    setDraggedCol(null)
    setDragOverCol(null)
  }, [])

  // --- CSV export ---
  const mapRowColumns = useCallback((inv: InvestorListItem, cols: ColumnDef[]): Record<string, unknown> => {
    const row: Record<string, unknown> = {}
    for (const col of cols) {
      const val = col.render(inv)
      row[col.label] = typeof val === 'string' ? val : ''
    }
    return row
  }, [])

  const csvDataDisplayed = useMemo(() => investors.map((inv) => mapRowColumns(inv, activeColumns)), [investors, activeColumns, mapRowColumns])
  const csvDataAll = useMemo(() => investors.map((inv) => mapRowColumns(inv, allColumns)), [investors, allColumns, mapRowColumns])

  const fetchAllForExport = useCallback(async (cols: ColumnDef[]): Promise<Record<string, unknown>[]> => {
    if (!token || !pagination) return []
    const allData: Record<string, unknown>[] = []
    for (let p = 1; p <= pagination.total_pages; p++) {
      const res = await fetchInvestorList(token, { ...apiParams, page: p })
      for (const inv of res.data) {
        allData.push(mapRowColumns(inv, cols))
      }
    }
    return allData
  }, [token, pagination, apiParams, mapRowColumns])

  // --- Styles ---
  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`,
    color: table.headerText, fontWeight: 500, fontSize: '13px', cursor: 'pointer',
    userSelect: 'none', whiteSpace: 'nowrap',
  }
  const tdStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, fontSize: '14px' }
  const tdStyleMuted: React.CSSProperties = { ...tdStyle, color: table.headerText }
  const dateStr = format(new Date(), 'yyyy-MM-dd')

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Filter Panel */}
        <div style={{
          border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px',
          backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: '12px',
        }}>
          {/* Row 1: Markets + Status (individual tab only) */}
          {tab === 'individual' && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ minWidth: '200px', flex: '1 1 200px', maxWidth: '280px' }}>
                <Multiselect
                  label="Markets"
                  options={groupedMarketOptions}
                  value={filterMarkets}
                  onValueChange={setFilterMarkets}
                  placeholder="All markets"
                  size="sm"
                  disabled={marketsLoading}
                />
              </div>
              <div style={{ minWidth: '200px', flex: '1 1 200px', maxWidth: '280px' }}>
                <Multiselect
                  label="Investor Status"
                  options={STATUS_OPTIONS}
                  value={filterStatuses}
                  onValueChange={setFilterStatuses}
                  placeholder="All statuses"
                  size="sm"
                />
              </div>
            </div>
          )}
          {/* Row 2: Onboarded + Last Login + Apply/Clear */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>Onboarded</label>
              <select
                value={onboardedTimeFrame}
                onChange={(e) => setOnboardedTimeFrame(e.target.value as TimeFramePreset)}
                style={{
                  width: '100%', padding: '7px 10px', border: '1px solid #DEDEE2',
                  borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                  backgroundColor: 'white', height: '36px', boxSizing: 'border-box',
                }}
              >
                {TIME_FRAME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {onboardedTimeFrame === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="From"
                      value={onboardedCustomStart}
                      onChange={setOnboardedCustomStart}
                      maxDate={onboardedCustomEnd ?? undefined}
                      size="sm"
                    />
                  </div>
                  <Text variant="bodySm" color="muted" style={{ paddingBottom: '10px' }}>{'\u2013'}</Text>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="To"
                      value={onboardedCustomEnd}
                      onChange={setOnboardedCustomEnd}
                      minDate={onboardedCustomStart ?? undefined}
                      maxDate={new Date()}
                      size="sm"
                    />
                  </div>
                </div>
              )}
            </div>
            <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>Last Login</label>
              <select
                value={loginTimeFrame}
                onChange={(e) => setLoginTimeFrame(e.target.value as TimeFramePreset)}
                style={{
                  width: '100%', padding: '7px 10px', border: '1px solid #DEDEE2',
                  borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                  backgroundColor: 'white', height: '36px', boxSizing: 'border-box',
                }}
              >
                {TIME_FRAME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {loginTimeFrame === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="From"
                      value={loginCustomStart}
                      onChange={setLoginCustomStart}
                      maxDate={loginCustomEnd ?? undefined}
                      size="sm"
                    />
                  </div>
                  <Text variant="bodySm" color="muted" style={{ paddingBottom: '10px' }}>{'\u2013'}</Text>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="To"
                      value={loginCustomEnd}
                      onChange={setLoginCustomEnd}
                      minDate={loginCustomStart ?? undefined}
                      maxDate={new Date()}
                      size="sm"
                    />
                  </div>
                </div>
              )}
            </div>
            <Button variant="primary" size="sm" onClick={handleApplyFilters}>Apply Filters</Button>
            <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
          </div>
        </div>

        {/* Tabs + Search + Column Picker + Export */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E5E7EB' }}>
            {([
              { value: 'individual' as const, label: 'Individual Investors' },
              { value: 'by-market' as const, label: 'By Market' },
            ] as const).map((t) => (
              <button
                key={t.value}
                onClick={() => updateSearch({ tab: t.value })}
                style={{
                  padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 500, fontFamily: 'SharpGrotesk, sans-serif',
                  color: tab === t.value ? '#1E40AF' : '#6B7280',
                  borderBottom: tab === t.value ? '2px solid #1E40AF' : '2px solid transparent',
                  marginBottom: '-2px',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Search */}
            {tab === 'individual' && (
              <div style={{ width: '240px', flexShrink: 0 }}>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search name, email, company..."
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid #DEDEE2',
                    borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                    outline: 'none', height: '36px', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
            {/* Column Picker */}
            {tab === 'individual' && (
              <div style={{ position: 'relative' }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowColumnPicker((v) => !v)}
                  aria-label="Configure columns"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM6.754 8a1.246 1.246 0 1 1 2.492 0 1.246 1.246 0 0 1-2.492 0z"/><path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.902 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283a.676.676 0 0 1 1.28 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16a.676.676 0 0 1 .906.906l-.16.292a1.873 1.873 0 0 0 1.116 2.692l.318.094a.676.676 0 0 1 0 1.28l-.318.094a1.873 1.873 0 0 0-1.116 2.693l.16.291a.676.676 0 0 1-.906.906l-.292-.16a1.873 1.873 0 0 0-2.692 1.116l-.094.318a.676.676 0 0 1-1.28 0l-.094-.318a1.873 1.873 0 0 0-2.693-1.116l-.291.16a.676.676 0 0 1-.906-.906l.16-.292a1.873 1.873 0 0 0-1.116-2.692l-.318-.094a.676.676 0 0 1 0-1.28l.318-.094a1.873 1.873 0 0 0 1.116-2.693l-.16-.291a.676.676 0 0 1 .906-.906l.292.16a1.873 1.873 0 0 0 2.692-1.116l.094-.318z"/></svg>
                  {' '}Columns
                </Button>
                {showColumnPicker && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setShowColumnPicker(false)} />
                    <div style={{
                      position: 'absolute', top: '100%', right: 0, zIndex: 10, marginTop: '4px',
                      backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 0', width: '220px',
                    }}>
                      {allColumns.filter((c) => c.optional).map((col) => (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
                            cursor: 'pointer', fontSize: '13px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(col.key)}
                            onChange={() => toggleColumn(col.key)}
                          />
                          {col.label}
                        </label>
                      ))}
                      <div style={{ borderTop: '1px solid #E5E7EB', padding: '6px 12px' }}>
                        <Button variant="primary" size="sm" onClick={() => setShowColumnPicker(false)} style={{ width: '100%' }}>
                          Done
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {tab === 'individual' ? (
              <ExportMenu options={[
                { label: 'Export Page — Displayed Columns', data: csvDataDisplayed as unknown as Record<string, unknown>[], filename: `investors-${dateStr}.csv` },
                { label: 'Export Page — All Columns', data: csvDataAll as unknown as Record<string, unknown>[], filename: `investors-all-cols-${dateStr}.csv` },
                ...(pagination && pagination.total_pages > 1 ? [
                  {
                    label: 'Export All — Displayed Columns',
                    description: `${pagination.total_count.toLocaleString()} investors`,
                    fetchData: () => fetchAllForExport(activeColumns),
                    filename: `investors-all-${dateStr}.csv`,
                  },
                  {
                    label: 'Export All — All Columns',
                    description: `${pagination.total_count.toLocaleString()} investors`,
                    fetchData: () => fetchAllForExport(allColumns),
                    filename: `investors-all-all-cols-${dateStr}.csv`,
                  },
                ] : []),
              ]} />
            ) : (
              <ExportMenu options={[
                { label: 'Export CSV', data: mapInvestorsByMarketPage(byMarketData), filename: investorsByMarketFilename() },
              ]} />
            )}
          </div>
        </div>

        {/* Individual Investors Tab */}
        {tab === 'individual' && (
          <div style={{ position: 'relative', minHeight: '400px' }}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                {pagination ? `${pagination.total_count.toLocaleString()} investors` : 'Loading...'}
              </Text>
            </div>

            {isFetching && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                <LoadingSpinner size="sm" />
              </div>
            )}

            {investors.length > 0 ? (
              <>
                <InfoTooltip.Provider>
                  <table className={styles.recordsTable}>
                    <thead>
                      <tr>
                        {activeColumns.map((col) => (
                          <th
                            key={col.key}
                            draggable
                            onDragStart={() => handleColDragStart(col.key)}
                            onDragOver={(e) => handleColDragOver(e, col.key)}
                            onDrop={() => handleColDrop(col.key)}
                            onDragEnd={handleColDragEnd}
                            style={{
                              ...thStyle,
                              textAlign: col.align ?? 'left',
                              opacity: draggedCol === col.key ? 0.4 : 1,
                              borderLeft: dragOverCol === col.key && draggedCol !== col.key ? '2px solid #3B82F6' : undefined,
                            }}
                            onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                          >
                            <ColumnHeader
                              label={col.label}
                              tooltip={COLUMN_TOOLTIPS[col.key] ?? col.label}
                              sortIndicator={col.sortKey ? sortIndicator(col.sortKey) : undefined}
                              align={col.align}
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {investors.map((inv) => (
                        <tr
                          key={inv.user_id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate({ to: '/admin/reporting/investors/$id', params: { id: inv.user_id } })}
                        >
                          {activeColumns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                ...(col.key === 'email' ? tdStyleMuted : tdStyle),
                                textAlign: col.align ?? 'left',
                              }}
                            >
                              {col.render(inv)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </InfoTooltip.Provider>

                {pagination && (
                  <div className={styles.paginationControls}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Text variant="bodyXs" color="muted">
                        Page {pagination.page} of {pagination.total_pages}
                        {' '}({pagination.total_count.toLocaleString()} investors)
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Text variant="bodyXs" color="muted">Show</Text>
                        <select
                          value={pageSize}
                          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                          style={{
                            padding: '2px 6px', border: '1px solid #DEDEE2', borderRadius: '4px',
                            fontSize: '12px', fontFamily: 'SharpGrotesk, sans-serif',
                            backgroundColor: 'white', cursor: 'pointer',
                          }}
                        >
                          {[10, 25, 50, 100, 200].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <Text variant="bodyXs" color="muted">per page</Text>
                      </div>
                    </div>
                    <Stack direction="row" gap="8">
                      <Button variant="outline" size="sm" disabled={isFetching || pagination.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={isFetching || !pagination.has_more} onClick={() => setPage((p) => p + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </div>
                )}
              </>
            ) : (
              !isFetching && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <Text variant="body" color="muted">No investors match the current filters</Text>
                </div>
              )
            )}
          </div>
        )}

        {/* By Market Tab */}
        {tab === 'by-market' && (
          <div style={{ position: 'relative', minHeight: '300px' }}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                {byMarketData.length > 0 ? `${byMarketData.length} markets` : 'Loading...'}
              </Text>
            </div>

            {byMarketFetching && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                <LoadingSpinner size="sm" />
              </div>
            )}

            {byMarketData.length > 0 ? (
              <>
                <InfoTooltip.Provider>
                  <table className={styles.recordsTable}>
                    <thead>
                      <tr>
                        <th style={thStyle}>
                          <ColumnHeader label="Market" tooltip={BY_MARKET_TOOLTIPS.market} />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Investors" tooltip={BY_MARKET_TOOLTIPS.investors} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Active" tooltip={BY_MARKET_TOOLTIPS.active} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Engaged" tooltip={BY_MARKET_TOOLTIPS.engaged} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Offers" tooltip={BY_MARKET_TOOLTIPS.offers} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Inquiries" tooltip={BY_MARKET_TOOLTIPS.inquiries} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="UC" tooltip={BY_MARKET_TOOLTIPS.uc} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Closed" tooltip={BY_MARKET_TOOLTIPS.closed} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Close Rate" tooltip={BY_MARKET_TOOLTIPS.close_rate} align="right" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {byMarketData.map((row: InvestorsByMarketItem) => {
                        const isSelected = selectedMarket === row.market
                        const cellClick = (type: DrillDownType) => {
                          if (isSelected && drillDownType === type) {
                            setSelectedMarket(null)
                          } else {
                            setSelectedMarket(row.market)
                            setDrillDownType(type)
                            setDrillDownPage(1)
                          }
                        }
                        const clickableTd = (type: DrillDownType): React.CSSProperties => ({
                          ...tdStyle,
                          textAlign: 'right',
                          cursor: 'pointer',
                          backgroundColor: isSelected && drillDownType === type ? '#DBEAFE' : isSelected ? '#F0F9FF' : undefined,
                        })
                        return (
                          <tr key={row.market} style={{ backgroundColor: isSelected ? '#F0F9FF' : undefined }}>
                            <td style={tdStyle}>{row.market}</td>
                            <td style={clickableTd('investors')} onClick={() => cellClick('investors')}>{row.investor_count.toLocaleString()}</td>
                            <td style={clickableTd('active')} onClick={() => cellClick('active')}>{row.active_count.toLocaleString()}</td>
                            <td style={clickableTd('engaged')} onClick={() => cellClick('engaged')}>{row.engaged_count.toLocaleString()}</td>
                            <td style={clickableTd('offers')} onClick={() => cellClick('offers')}>{row.total_offers.toLocaleString()}</td>
                            <td style={clickableTd('inquiries')} onClick={() => cellClick('inquiries')}>{row.total_inquiries.toLocaleString()}</td>
                            <td style={clickableTd('uc')} onClick={() => cellClick('uc')}>{row.contracts_uc.toLocaleString()}</td>
                            <td style={clickableTd('closed')} onClick={() => cellClick('closed')}>{row.closed.toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avg_close_rate !== null ? `${row.avg_close_rate}%` : 'N/A'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    {(() => {
                      const totals = byMarketData.reduce((acc, row) => ({
                        investor_count: acc.investor_count + row.investor_count,
                        active_count: acc.active_count + row.active_count,
                        engaged_count: acc.engaged_count + row.engaged_count,
                        total_offers: acc.total_offers + row.total_offers,
                        total_inquiries: acc.total_inquiries + row.total_inquiries,
                        contracts_uc: acc.contracts_uc + row.contracts_uc,
                        closed: acc.closed + row.closed,
                      }), { investor_count: 0, active_count: 0, engaged_count: 0, total_offers: 0, total_inquiries: 0, contracts_uc: 0, closed: 0 })
                      const totalCloseRate = (totals.contracts_uc + totals.closed) > 0
                        ? (totals.closed / (totals.contracts_uc + totals.closed)) * 100
                        : null
                      const tfStyle: React.CSSProperties = { ...tdStyle, fontWeight: 600, borderTop: '2px solid #D1D5DB' }
                      return (
                        <tfoot>
                          <tr>
                            <td style={tfStyle}>Total</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.investor_count.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.active_count.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.engaged_count.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.total_offers.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.total_inquiries.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.contracts_uc.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.closed.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totalCloseRate !== null ? `${totalCloseRate.toFixed(1)}%` : 'N/A'}</td>
                          </tr>
                        </tfoot>
                      )
                    })()}
                  </table>
                </InfoTooltip.Provider>

                {/* Drill-Down Panel */}
                {selectedMarket && (
                  <div className={styles.drillDownPanel} style={{ marginTop: '16px' }}>
                    <div className={styles.drillDownHeader}>
                      <Text variant="body" style={{ fontWeight: 600 }}>
                        {isInvestorDrill
                          ? `${drillDownType === 'investors' ? 'Investors' : drillDownType === 'active' ? 'Active Investors' : 'Engaged Investors'} in ${selectedMarket}`
                          : `${drillDownType === 'offers' ? 'Offers' : drillDownType === 'inquiries' ? 'Inquiries' : drillDownType === 'uc' ? 'Under Contract' : 'Closed Deals'} in ${selectedMarket}`
                        }
                        {drillDownPagination && (
                          <Text as="span" variant="bodySm" color="muted" style={{ fontWeight: 400, marginLeft: '8px' }}>
                            ({drillDownPagination.total_count.toLocaleString()})
                          </Text>
                        )}
                      </Text>
                      <button
                        onClick={() => setSelectedMarket(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: '#6B7280' }}
                        aria-label="Close drill-down"
                      >
                        <Icon name="close" size={18} />
                      </button>
                    </div>

                    <div style={{ position: 'relative', minHeight: '200px' }}>
                      {drillDownFetching && (
                        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                          <LoadingSpinner size="sm" />
                        </div>
                      )}

                      {/* Investor drill-down table (investors/active/engaged) */}
                      {isInvestorDrill && (() => {
                        const data = investorDrillRes?.data ?? []
                        return data.length > 0 ? (
                          <>
                            <table className={styles.recordsTable}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Investor Name</th>
                                  <th style={thStyle}>Company</th>
                                  <th style={thStyle}>Email</th>
                                  <th style={thStyle}>Phone</th>
                                  <th style={thStyle}>Buy Box Markets</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.map((inv: InvestorListItem) => (
                                  <tr
                                    key={inv.user_id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate({ to: '/admin/reporting/investors/$id', params: { id: inv.user_id } })}
                                  >
                                    <td style={tdStyle}>{inv.first_name} {inv.last_name}</td>
                                    <td style={tdStyle}>{inv.company_name ?? '-'}</td>
                                    <td style={tdStyleMuted}>{inv.email ?? '-'}</td>
                                    <td style={tdStyle}>{inv.phone ?? '-'}</td>
                                    <td style={tdStyle}>{inv.buybox_markets?.join(', ') ?? '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {drillDownPagination && drillDownPagination.total_pages > 1 && (
                              <div className={styles.paginationControls}>
                                <Text variant="bodyXs" color="muted">
                                  Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                </Text>
                                <Stack direction="row" gap="8">
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>
                                    Previous
                                  </Button>
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>
                                    Next
                                  </Button>
                                </Stack>
                              </div>
                            )}
                          </>
                        ) : (
                          !drillDownFetching && (
                            <div style={{ textAlign: 'center', padding: '24px' }}>
                              <Text variant="body" color="muted">No investors found</Text>
                            </div>
                          )
                        )
                      })()}

                      {/* Activity drill-down table (offers/inquiries/uc/closed) */}
                      {isActivityDrill && (() => {
                        const data = activityDrillRes?.data ?? []
                        const showValue = drillDownType !== 'inquiries'
                        return data.length > 0 ? (
                          <>
                            <table className={styles.recordsTable}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Investor Name</th>
                                  <th style={thStyle}>Company</th>
                                  <th style={thStyle}>Property Address</th>
                                  <th style={thStyle}>Market</th>
                                  <th style={thStyle}>Agent</th>
                                  {showValue && <th style={{ ...thStyle, textAlign: 'right' }}>Offer Price</th>}
                                  <th style={thStyle}>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.map((rec: MarketActivityRecordItem) => (
                                  <tr key={rec.id}>
                                    <td style={tdStyle}>{rec.investor_name ?? '-'}</td>
                                    <td style={tdStyle}>{rec.company_name ?? '-'}</td>
                                    <td style={tdStyle}>{rec.property_address ?? '-'}</td>
                                    <td style={tdStyle}>{rec.market ?? '-'}</td>
                                    <td style={tdStyle}>{rec.agent_name ?? '-'}</td>
                                    {showValue && <td style={{ ...tdStyle, textAlign: 'right' }}>{rec.value != null ? `$${rec.value.toLocaleString()}` : '-'}</td>}
                                    <td style={tdStyle}>{rec.date ? formatDate(rec.date) : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {drillDownPagination && drillDownPagination.total_pages > 1 && (
                              <div className={styles.paginationControls}>
                                <Text variant="bodyXs" color="muted">
                                  Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                </Text>
                                <Stack direction="row" gap="8">
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>
                                    Previous
                                  </Button>
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>
                                    Next
                                  </Button>
                                </Stack>
                              </div>
                            )}
                          </>
                        ) : (
                          !drillDownFetching && (
                            <div style={{ textAlign: 'center', padding: '24px' }}>
                              <Text variant="body" color="muted">No records found</Text>
                            </div>
                          )
                        )
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : (
              !byMarketFetching && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <Text variant="body" color="muted">No market data available for the current filters</Text>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}
